import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { WhatsAppNumberForm } from "@/components/admin/WhatsAppNumberForm";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";
import { useAdminSession } from "@/hooks/use-admin-session";
import {
  allFeatureFlagsQueryOptions,
  setFeatureFlag,
  type FeatureFlag,
} from "@/lib/feature-flags.functions";

export const Route = createFileRoute("/_admin/admin/feature-flags")({
  head: () => ({
    meta: [
      { title: "Feature-Flags – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;
    const { data: isAdmin } = await supabase.rpc("has_role", {
      _user_id: data.user.id,
      _role: "admin",
    });
    if (!isAdmin) {
      const { redirect } = await import("@tanstack/react-router");
      throw redirect({ to: "/admin" });
    }
  },
  component: FeatureFlagsPage,
});

function FeatureFlagsPage() {
  const session = useAdminSession();
  const queryClient = useQueryClient();
  const setFlag = useServerFn(setFeatureFlag);
  const { data, isLoading } = useQuery(allFeatureFlagsQueryOptions());

  const mutation = useMutation({
    mutationFn: (vars: { key: string; enabled: boolean }) =>
      setFlag({ data: vars }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ["feature-flags"] });
      const previous = queryClient.getQueryData<FeatureFlag[]>(["feature-flags", "all"]);
      queryClient.setQueryData<FeatureFlag[]>(["feature-flags", "all"], (old) =>
        (old ?? []).map((f) =>
          f.key === vars.key ? { ...f, enabled: vars.enabled } : f,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(["feature-flags", "all"], ctx.previous);
      }
      toast.error("Feature-Flag konnte nicht gespeichert werden.");
    },
    onSuccess: (_res, vars) => {
      toast.success(
        vars.enabled ? "Feature aktiviert." : "Feature deaktiviert.",
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
    },
  });

  if (session.status !== "loading" && !session.isAdmin) {
    return (
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-3xl font-extrabold">Kein Zugriff</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Feature-Flags dürfen ausschließlich von Administratoren geändert werden.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary">
          Feature-Flags
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Aktivieren oder deaktivieren Sie Module der Website. Änderungen
          wirken sofort. Nur Administratoren können hier Anpassungen vornehmen.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Flags werden geladen …</p>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Aktuell sind keine Feature-Flags konfiguriert.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {data.map((flag) => (
            <li
              key={flag.key}
              className="flex items-start justify-between gap-6 px-5 py-4"
            >
              <div className="min-w-0">
                <p className="font-display text-base font-extrabold text-foreground">
                  {flag.label}
                </p>
                {flag.description && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {flag.description}
                  </p>
                )}
                <p className="mt-2 font-mono text-[11px] text-muted-foreground/70">
                  {flag.key}
                </p>
                {flag.key === FEATURE_FLAGS.whatsappButtonVisible && <WhatsAppNumberForm />}
              </div>
              <div className="pt-1">
                <Switch
                  checked={flag.enabled}
                  disabled={mutation.isPending}
                  onCheckedChange={(next) =>
                    mutation.mutate({ key: flag.key, enabled: next })
                  }
                  aria-label={`${flag.label} umschalten`}
                />
              </div>
            </li>
          ))}
          {/* Bis der Schalter angelegt ist, bleibt die Nummer trotzdem pflegbar. */}
          {!data.some((f) => f.key === FEATURE_FLAGS.whatsappButtonVisible) && (
            <li className="px-5 py-4">
              <p className="font-display text-base font-extrabold text-foreground">
                WhatsApp-Button „Kurze Frage?“
              </p>
              <WhatsAppNumberForm />
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
